import React, { useRef, useEffect } from 'react';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import Tooltip from '../common/Tooltip';

interface MessageInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onKeyPress?: (e: React.KeyboardEvent) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  maxLength?: number;
  showCharCount?: boolean;
}

const MessageInput: React.FC<MessageInputProps> = ({
  value,
  onChange,
  onSend,
  onKeyPress,
  disabled = false,
  placeholder = 'Type your message...',
  className = '',
  maxLength = 4000,
  showCharCount = true
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, [value]);

  // Focus on mount
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= maxLength) {
      onChange(newValue);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (onKeyPress) {
      onKeyPress(e);
    }
  };

  const isNearLimit = value.length > maxLength * 0.9;
  const remainingChars = maxLength - value.length;

  return (
    <div className={`relative ${className}`}>
      <div className="flex items-end gap-2">
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            disabled={disabled}
            className={`
              w-full px-4 py-3 pr-12
              border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white
              resize-none
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
              disabled:bg-gray-100 dark:disabled:bg-gray-900 disabled:cursor-not-allowed
              transition-colors duration-200
              ${disabled ? 'opacity-50' : ''}
            `}
            rows={1}
            style={{
              minHeight: '48px',
              maxHeight: '150px'
            }}
          />

          {/* Character count indicator */}
          {showCharCount && value.length > 0 && (
            <div
              className={`
                absolute bottom-2 right-12 text-xs
                ${isNearLimit ? 'text-orange-500' : 'text-gray-400'}
              `}
            >
              {remainingChars}
            </div>
          )}
        </div>

        {/* Send button */}
        <Tooltip content="Send message">
        <button
          onClick={onSend}
          disabled={disabled || !value.trim()}
          className={`
            p-3 rounded-lg
            transition-all duration-200
            ${disabled || !value.trim()
              ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
              : 'bg-blue-500 hover:bg-primary-600 active:scale-95'
            }
          `}
        >
          {disabled ? (
            <ArrowPathIcon className="w-5 h-5 text-white animate-spin" />
          ) : (
            <PaperAirplaneIcon className="w-5 h-5 text-white" />
          )}
        </button>
        </Tooltip>
      </div>

      {/* Quick actions / suggestions */}
      {!disabled && value.length === 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          <QuickAction onClick={() => onChange('Hello!')} text="Hello!" />
          <QuickAction onClick={() => onChange('Can you help me with...')} text="Help me..." />
          <QuickAction onClick={() => onChange('What can you do?')} text="What can you do?" />
        </div>
      )}
    </div>
  );
};

// Quick action button component
const QuickAction: React.FC<{ onClick: () => void; text: string }> = ({ onClick, text }) => {
  return (
    <button
      onClick={onClick}
      className="
        px-3 py-1 text-sm text-gray-600 dark:text-gray-400
        bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700
        rounded-full transition-colors duration-200
        border border-gray-300 dark:border-gray-600
      "
    >
      {text}
    </button>
  );
};

export default MessageInput;